/**
 * How a NEAR Intents token is named on the page.
 *
 * A symbol alone does not identify a token there: USDC bridged from Ethereum,
 * Solana, Base and Arbitrum are four different tokens that all call themselves
 * USDC, and a market page listing "wNEAR in USDC" six times says nothing about
 * which is which. The bridge's own contract name does, and it is the identity
 * anyway — a symbol is self-reported, and anyone can deploy a token calling
 * itself USDC (blackbeard/observer#27).
 */

/** Native NEAR tokens worth naming by chain, because they share a symbol with a bridged one. */
const NATIVE: Record<string, string> = {
  '17208628f84f5d6ad33f0da3bbbeb27ffcb398eac501a31bd6ad2011e36133a1': 'near',
  'usdt.tether-token.near': 'near',
  'wrap.near': 'near',
}

/** The contract part of a multi-token id: `nep141:eth-0x….omft.near` → `eth-0x….omft.near`. */
export function contractOf(tokenId: string): string {
  return tokenId.replace(/^nep\d+:/, '')
}

/**
 * Where a token comes from, when its contract says: the chain prefix of a
 * PoA (`*.omft.near`) or omni bridge token, `near` for a few native ones whose
 * symbols collide with bridged tokens, and null otherwise.
 */
export function originOf(tokenId: string): string | null {
  const contract = contractOf(tokenId)
  if (NATIVE[contract]) return NATIVE[contract]
  const bridged = /^([a-z0-9]+)(?:-[^.]*)?\.(?:omft|omdep)\.near$/.exec(contract)
  if (bridged) return bridged[1] ?? null
  return null
}

/** A short contract name for a cell, keeping both ends. */
export function shortContract(tokenId: string): string {
  const contract = contractOf(tokenId)
  return contract.length > 22 ? `${contract.slice(0, 9)}…${contract.slice(-9)}` : contract
}

/** `USDC · eth`, or the symbol alone, or a short contract when there is no symbol. */
export function tokenLabel(tokenId: string, symbol: string | null | undefined): string {
  if (!symbol) return shortContract(tokenId)
  const origin = originOf(tokenId)
  // A chain's own coin needs no qualifier: `SOL · sol` and `wNEAR · near` say
  // nothing the symbol does not. Only a token that could be confused with
  // another gets one.
  return origin && !symbol.toLowerCase().endsWith(origin) ? `${symbol} · ${origin}` : symbol
}
